/**
 * @graphql cc
 */
import type { FC } from 'react';
import React, { useState, useEffect, useCallback } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
import { Subscribe } from 'unstated';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { styled } from '@compiled/react';
import { gql } from '@apollo/client';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { OptionalEmojiDescription } from '@atlaskit/emoji/types';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import { getDraftShareIdFromQueryParam } from '@confluence/route-manager-utils/entry-points/getDraftShareIdFromQueryParam';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { withPageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container/entry-points/withPageTreeStateUpdater';
import { expValEquals } from '@confluence/feature-experiments';
import { setContentErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { useUpdateContentProperty } from '@confluence/update-content-property-utils/entry-points/useUpdateContentProperty';

import { trackDraftCreatedWithEmoji } from '../analytics';
import { EmojiPickerLoader } from '../EmojiPickerLoader';
import { getEmojiFromEmojiId, onRemoveEmoji } from '../emojiMutationHelpers';
import { getHasEmoji, getEmojiId, getHasLiveDocsDefaultEmoji } from '../transformer';
import { useEmojiStatusFieldName } from '../useEmojiStatusFieldName';

import type {
	EmojiTitleButtonComponentProps,
	UpdateDefaultEmojiArgs,
} from './__types__/EmojiTitleButton';
import { EmojiTitlePopover } from './EmojiTitlePopover';
import { EmojiTitleStateContainer } from './EmojiTitleStateContainer';
// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
import { UpdateContentPropertyMutation } from './EmojiTitleSectionMutation.experimentalgraphql';
import { getEmojiTitleProperty } from './EmojiTitleQuery.graphql';
import type { getEmojiTitleProperty as getEmojiTitlePropertyType } from './__types__/getEmojiTitleProperty';
import {
	ConfluenceGraphQLDefaultTitleEmoji as DefaultTitleEmojiValues,
	type EmojiTitleButtonUpdateDefaultTitleEmojiMutation_confluence_updateDefaultTitleEmoji_ConfluenceUpdateDefaultTitleEmojiPayload_errors_MutationError as MutationErrorType,
} from './__types__/EmojiTitleButtonUpdateDefaultTitleEmojiMutation';
import type {
	UpdateContentPropertyMutation as UpdateContentPropertyMutationType,
	UpdateContentPropertyMutationVariables,
} from './__types__/UpdateContentPropertyMutation';

const EmojiTitleButtonUpdateDefaultTitleEmojiMutation = gql`
	mutation EmojiTitleButtonUpdateDefaultTitleEmojiMutation(
		$cloudId: ID!
		$input: ConfluenceUpdateDefaultTitleEmojiInput!
	) {
		confluence_updateDefaultTitleEmoji(cloudId: $cloudId, input: $input) {
			contentId
			success
			errors {
				message
				extensions {
					errorType
					statusCode
				}
			}
		}
	}
`;

const processDefaultTitleMutationError = (
	error: Error | ApolloErrorInterop | null,
	contentId: string,
) => {
	setContentErrorAttributes(error, {
		objectId: contentId,
	});
	getMonitoringClient().submitError(error, {
		attribution: Attribution.PAGE_MODES,
	});
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const EmojiWrapper = styled.div({
	marginTop: token('space.025'),
	paddingRight: token('space.075'),
	minHeight: '40px',
});

const EmojiTitleButtonComponent: FC<EmojiTitleButtonComponentProps> = ({
	contentId,
	onUpdateStartEmoji,
	onUpdateEndEmoji,
	emojiTitleState,
	setSelectedEmoji,
	onEmojiPickerLoaded,
	isUnpublishedDraft,
	disableEmoji,
	floatingToolbarButtonStyle,
	pageTreeStateUpdater,
	contentType,
	autoPageEmojiCohort,
	emojiTitleDiscovered,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId, userId } = useSessionData();
	const isLivePage = useIsCurrentPageLive();
	const emojiStatusFieldName = useEmojiStatusFieldName();
	const [defaulTitleEmojiMutationError, setDefaulTitleEmojiMutationError] = useState<
		Error | ApolloErrorInterop | null
	>(null);

	const setSelectedEmojiCallback = useCallback(
		(emoji: any) => {
			setSelectedEmoji && setSelectedEmoji(emoji);
		},
		[setSelectedEmoji],
	);

	const [savedEmoji, setSavedEmoji] = useState<
		OptionalEmojiDescription | Promise<OptionalEmojiDescription> | null
	>(null);

	const closeBothEmojiPicker = () => {
		void emojiTitleState.setOpenFirstEmojiPicker(false);
		void emojiTitleState.setOpenSecondEmojiPicker(false);
	};

	const updateIsRemovingEmoji = () => {
		void emojiTitleState.setIsRemovingEmoji(true);
	};

	const onClickInEditor = useCallback((event: any) => {
		const emojiDialog = document.getElementById('emoji-picker');
		const EMOJI_POPUP_BUTTOM_ID = 'emoji-popup-button';

		const shouldCloseDialog = !(
			event.target?.classList?.contains('emoji') ||
			event.target?.classList?.contains('emoji-common-emoji-sprite') ||
			event.target?.classList?.contains('emoji-popup-button-text') ||
			event.target?.getAttribute('aria-label') === 'Add emoji' ||
			event.target?.parentNode?.classList?.contains('cover-picture') ||
			event.target?.getAttribute('aria-label') === 'Emoji' ||
			event.target?.classList?.contains('add-picture-button') ||
			event.target?.getAttribute('id') === EMOJI_POPUP_BUTTOM_ID ||
			event.target?.parentNode?.getAttribute('id') === EMOJI_POPUP_BUTTOM_ID ||
			event.target?.parentNode?.classList?.contains(EMOJI_POPUP_BUTTOM_ID) ||
			event.target?.parentNode?.classList?.contains('emoji-picker-add-emoji') ||
			event.target?.classList?.contains('add-picture-button') ||
			event.target?.getAttribute('aria-label') === 'image icon' ||
			// The following attributes come from EmojiTitleSelection and are used to ensure
			// the default icon's elements and its container are not registered as editor clicks
			event.target?.parentNode?.getAttribute('data-testid') === 'live-docs-default-title-icon' ||
			event.target?.getAttribute('data-testid') === 'live-docs-default-title-icon'
		);

		if (!emojiDialog?.contains(event.target) && shouldCloseDialog) {
			closeBothEmojiPicker();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		// handle close emoji picker when user click outside the picker dialog
		document.addEventListener('click', onClickInEditor);

		return () => {
			document.removeEventListener('click', onClickInEditor);
			closeBothEmojiPicker();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		return () => {
			if (isUnpublishedDraft) {
				// track draft created with emoji
				void trackDraftCreatedWithEmoji({
					createAnalyticsEvent,
					contentId,
					isLivePage,
				});
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isUnpublishedDraft]);

	const {
		data,
		error,
		refetch: refetchEmoji,
	} = useQuery<getEmojiTitlePropertyType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
		getEmojiTitleProperty,
		{
			variables: {
				contentId,
				status: ['current', 'draft'],
				skipPublishedFragment: false,
				draftShareId: getDraftShareIdFromQueryParam(),
			},
		},
	);

	const {
		updateContentProperty,
		updateContentPropertyResult: { loading: updateEmojiLoading } = {},
	} = useUpdateContentProperty({
		contentId,
		contentType: contentType || 'page',
		options: {
			refetchQueries: ['getEmojiTitlePropertyQuery'],
		},
	});

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const [updateContentPropertyExperimentalMutation, { loading: updateEmojiExperimentalLoading }] =
		useMutation<UpdateContentPropertyMutationType, UpdateContentPropertyMutationVariables>(
			// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Experimental Schema GraphQL Operation
			UpdateContentPropertyMutation,
			{
				refetchQueries: ['getEmojiTitlePropertyQuery'],
			},
		);

	const [updateDefaultTitleEmojiMutation, { loading: updateDefaultTitleEmojiLoading }] =
		// eslint-disable-next-line graphql-client-usage/graphql-generic-types
		useMutation(EmojiTitleButtonUpdateDefaultTitleEmojiMutation, {
			refetchQueries: ['getEmojiTitlePropertyQuery'],
			awaitRefetchQueries: true,
		});

	const updateDefaultEmoji = useCallback(
		async ({ defaultTitleEmoji, onUpdateComplete }: UpdateDefaultEmojiArgs) => {
			if (!isLivePage) {
				return;
			}

			try {
				void emojiTitleState.setIsUpdatingEmoji(true);
				const { data: mutationData } = await updateDefaultTitleEmojiMutation({
					variables: {
						cloudId,
						input: {
							contentId,
							defaultTitleEmoji,
						},
					},
				});
				const result = mutationData?.confluence_updateDefaultTitleEmoji;
				if (result?.success) {
					void emojiTitleState.setHasLiveDocsDefaultEmoji(
						defaultTitleEmoji === 'LIVE_PAGE_DEFAULT',
					);
					setDefaulTitleEmojiMutationError(null);
					onUpdateComplete?.();
				}
				if (result?.errors?.length && result?.errors?.length > 0) {
					result.errors.forEach((mutationError: MutationErrorType) => {
						if (
							mutationError.message &&
							mutationError.extensions &&
							mutationError.extensions[0]?.statusCode
						) {
							const parsedError = new Error(mutationError?.message || 'Unknown error');
							setDefaulTitleEmojiMutationError(parsedError);
							processDefaultTitleMutationError(parsedError, contentId);
						}
					});
				}
			} catch (gqlError) {
				setDefaulTitleEmojiMutationError(gqlError);
				processDefaultTitleMutationError(gqlError, contentId);
				markErrorAsHandled(gqlError);
			} finally {
				void emojiTitleState.setIsUpdatingEmoji(false);
			}
		},
		[isLivePage, contentId, cloudId, updateDefaultTitleEmojiMutation, emojiTitleState],
	);

	const refetchEmojiAndUpdatePageTreeSubscription = useCallback(async () => {
		await refetchEmoji();
		if (isLivePage) pageTreeStateUpdater.setForceFetchPageId(contentId);
	}, [refetchEmoji, isLivePage, pageTreeStateUpdater, contentId]);

	useSubscriptionEvent({
		contentId,
		contentType,
		delta: 'emojiTitleUpdated',
		subscriberId: 'emojiTitleButton',
		onDataReceived: refetchEmojiAndUpdatePageTreeSubscription,
	});

	useSubscriptionEvent({
		contentId,
		contentType,
		delta: 'emojiTitleDeleted',
		subscriberId: 'emojiTitleButtonDeleted',
		onDataReceived: refetchEmojiAndUpdatePageTreeSubscription,
	});

	const hasEmoji = !!(data && getHasEmoji(data, emojiStatusFieldName));
	const emojiId = (data && getEmojiId(data, emojiStatusFieldName)) ?? '';
	const hasDefaultTitleEmoji = isLivePage && getHasLiveDocsDefaultEmoji(data);

	const removeEmojiCallback = async (
		event: React.MouseEvent<Element | HTMLDivElement | HTMLTextAreaElement>,
	) => {
		setSavedEmoji(null);
		setSelectedEmojiCallback(null);
		closeBothEmojiPicker();
		updateIsRemovingEmoji();

		if (isLivePage) {
			// Default Emoji Removal
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'removeDefaultEmojiButton',
					actionSubjectId: 'removeDefaultEmojiButton_clicked',
					source: 'EmojiTitleButton',
					attributes: {
						contentId,
						isLivePage,
						hasLiveDocsDefaultEmoji: expValEquals(
							'platform_editor_hydratable_ui',
							'isEnabled',
							true,
						)
							? hasDefaultTitleEmoji
							: emojiTitleState.state.hasLiveDocsDefaultEmoji,
					},
				},
			}).fire();

			void updateDefaultEmoji({ defaultTitleEmoji: DefaultTitleEmojiValues.NONE });
		}

		if (hasEmoji) {
			await onRemoveEmoji(event, {
				onUpdateStartEmoji,
				onUpdateEndEmoji,
				onEmojiUpdated: refetchEmoji,
				contentId,
				updateContentPropertyExperimentalMutation,
				updateContentProperty,
				componentName: 'EmojiTitleButton',
				emojiStatusFieldName,
			});
		} else {
			// Handle clean up if not handled in onRemoveEmoji call
			onUpdateEndEmoji?.();
		}
	};

	useEffect(() => {
		if (hasEmoji && emojiId.length > 0) {
			void getEmojiFromEmojiId({
				userId: userId!,
				cloudId,
				emojiId,
			}).then((emoji) => {
				emoji && setSavedEmoji(emoji);
				emoji && setSelectedEmojiCallback(emoji);
			});
		} else {
			setSelectedEmojiCallback(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasEmoji, emojiId]);

	return (
		<ErrorBoundary attribution={Attribution.PAGE_EXTENSIONS}>
			{error ? <ErrorDisplay error={error} /> : null}
			{defaulTitleEmojiMutationError ? (
				<ErrorDisplay error={defaulTitleEmojiMutationError} />
			) : null}
			<EmojiTitlePopover
				key="editor-emoji-title"
				contentId={contentId}
				floatingToolbarButtonStyle={floatingToolbarButtonStyle}
				isLoading={
					updateEmojiExperimentalLoading ||
					updateEmojiLoading ||
					emojiTitleState.state.isRemovingEmoji ||
					updateDefaultTitleEmojiLoading
				}
				setOpenSecondEmojiPicker={emojiTitleState.setOpenSecondEmojiPicker}
				openSecondEmojiPicker={emojiTitleState.state.openSecondEmojiPicker}
				setIsEmojiButtonFocused={emojiTitleState.setIsEmojiButtonFocused}
				userOffline={disableEmoji}
				emojiPicker={
					<EmojiPickerLoader
						contentId={contentId}
						onUpdateStartEmoji={onUpdateStartEmoji}
						emojiTitleState={emojiTitleState}
						onEmojiUpdated={refetchEmoji}
						onUpdateEndEmoji={onUpdateEndEmoji}
						updateContentPropertyExperimentalMutation={updateContentPropertyExperimentalMutation}
						updateContentProperty={updateContentProperty}
						closeBothEmojiPicker={closeBothEmojiPicker}
						componentName="EmojiTitleButton"
						autoPageEmojiCohort={autoPageEmojiCohort}
					/>
				}
				onEmojiPickerLoaded={onEmojiPickerLoaded}
				hasEmoji={hasEmoji && !!savedEmoji}
				onRemoveEmoji={removeEmojiCallback}
				updateDefaultEmoji={updateDefaultEmoji}
				hasLiveDocsDefaultEmoji={
					expValEquals('platform_editor_hydratable_ui', 'isEnabled', true)
						? hasDefaultTitleEmoji
						: emojiTitleState.state.hasLiveDocsDefaultEmoji
				}
				autoPageEmojiCohort={autoPageEmojiCohort}
				emojiTitleDiscovered={emojiTitleDiscovered}
				contentType={contentType ?? undefined}
			/>
		</ErrorBoundary>
	);
};

const EmojiTitleButtonWithPTSU = withPageTreeStateUpdater(EmojiTitleButtonComponent);

export const EmojiTitleButton = (
	props: Omit<EmojiTitleButtonComponentProps, 'emojiTitleState' | 'pageTreeStateUpdater'>,
) => (
	<Subscribe to={[EmojiTitleStateContainer]}>
		{(emojiTitleState: EmojiTitleStateContainer) => (
			<EmojiTitleButtonWithPTSU
				{...props}
				emojiTitleState={emojiTitleState}
				data-testid="emoji-title-button"
			/>
		)}
	</Subscribe>
);
